import React, { useState, useEffect } from 'react';
import { risksAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const RisksPage = () => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    likelihood: 3,
    impact: 3,
    category: '',
    owner: '',
    mitigation_plan: ''
  });

  useEffect(() => {
    fetchRisks();
  }, []);

  const fetchRisks = async () => {
    try {
      const response = await risksAPI.list();
      setRisks(response.data);
    } catch (error) {
      toast.error('Failed to load risks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await risksAPI.create({
        ...formData,
        likelihood: parseInt(formData.likelihood),
        impact: parseInt(formData.impact)
      });
      toast.success('Risk created successfully');
      setIsDialogOpen(false);
      setFormData({ title: '', description: '', likelihood: 3, impact: 3, category: '', owner: '', mitigation_plan: '' });
      fetchRisks();
    } catch (error) {
      toast.error('Failed to create risk');
    }
  };

  const getRiskColor = (level) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[level] || colors.low;
  };

  // Risk heatmap data
  const heatmapData = Array.from({ length: 5 }, (_, impact) =>
    Array.from({ length: 5 }, (_, likelihood) => {
      const score = (5 - impact) * (likelihood + 1);
      return { impact: 5 - impact, likelihood: likelihood + 1, score };
    })
  ).flat();

  const getHeatmapColor = (score) => {
    if (score >= 20) return '#DC2626';
    if (score >= 12) return '#EA580C';
    if (score >= 6) return '#D97706';
    return '#16A34A';
  };

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="risks-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">Risk Management</h1>
          <p className="text-sm text-muted-foreground">Identify, assess, and mitigate organizational risks</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-risk-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Risk
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Risk</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Risk Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="risk-title-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                  data-testid="risk-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="likelihood">Likelihood (1-5)</Label>
                  <Input
                    id="likelihood"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.likelihood}
                    onChange={(e) => setFormData({ ...formData, likelihood: e.target.value })}
                    required
                    data-testid="risk-likelihood-input"
                  />
                </div>
                <div>
                  <Label htmlFor="impact">Impact (1-5)</Label>
                  <Input
                    id="impact"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.impact}
                    onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                    required
                    data-testid="risk-impact-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  data-testid="risk-category-input"
                />
              </div>
              <div>
                <Label htmlFor="owner">Risk Owner</Label>
                <Input
                  id="owner"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  required
                  data-testid="risk-owner-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-risk-button">Create Risk</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Risk Heatmap */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold font-heading">Risk Heatmap</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-medium">Impact</span>
                <div className="flex-1" />
              </div>
              <div className="grid grid-cols-6 gap-2">
                <div />
                {[1, 2, 3, 4, 5].map((l) => (
                  <div key={l} className="text-center text-xs font-medium text-muted-foreground">
                    {l}
                  </div>
                ))}
                {[5, 4, 3, 2, 1].map((imp) => (
                  <React.Fragment key={imp}>
                    <div className="flex items-center justify-center text-xs font-medium text-muted-foreground">
                      {imp}
                    </div>
                    {[1, 2, 3, 4, 5].map((lik) => {
                      const score = imp * lik;
                      const color = getHeatmapColor(score);
                      return (
                        <div
                          key={`${imp}-${lik}`}
                          className="aspect-square flex items-center justify-center text-xs font-semibold text-white rounded"
                          style={{ backgroundColor: color }}
                        >
                          {score}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                <div />
                <div className="col-span-5 text-center text-sm font-medium mt-2">Likelihood</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Risk List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : risks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No risks found. Create your first risk assessment.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {risks.map((risk) => (
            <Card key={risk.id} className="hover:shadow-md transition-shadow" data-testid={`risk-card-${risk.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{risk.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskColor(risk.risk_level)}`}>
                        {risk.risk_level.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{risk.description}</p>
                    <div className="flex items-center gap-6 text-xs text-muted-foreground">
                      <span>Category: {risk.category}</span>
                      <span>Owner: {risk.owner}</span>
                      <span>Status: {risk.status}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold font-heading mb-1">{risk.risk_score}</div>
                    <div className="text-xs text-muted-foreground">Risk Score</div>
                    <div className="mt-2 text-xs">
                      <div>L: {risk.likelihood} × I: {risk.impact}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RisksPage;